<template>
  <div id="personalDetails" class="personalDetails">
    <ValidationObserver ref="observer" @submit.prevent="close()" slim>
      <div class="form" :class="[$mq, {TA:isTestAchat}]">
        <h1>{{ $t("Contract.PersonalDetails.title") }}
          <span class="close" @click="close()"></span>
        </h1>
        <ValidationObserver v-slot="{ errors, failed }" slim>
          <div class="multi-field inline left" :class="{'error': failed}">
            <BecomeField
              name="civility"
              type="switch"
              v-model="civility"
              :possibleValues="civilities"
              silentError
            ></BecomeField>
            <BecomeField
              name="last_name"
              :label="$t('Situation.PersonalDetails.lastname')"
              class="flex lastname"
              :class="$mq"
              type="text"
              rules="min:2|max:64|alpha"
              :value="lastName | capitalize"
              @input="lastName = $event"
              required
              silentError
            ></BecomeField>
            <div class="error-block" v-if="Object.keys(errors).length">
              <span v-if="errors['last_name'].length">{{ errors['last_name'][0] }}</span>
              <span v-else>{{ errors['civility'][0] }}</span>
            </div>
          </div>
        </ValidationObserver>

        <BecomeField
          name="first_name"
          :label="$t('Situation.PersonalDetails.firstname')"
          :value="firstName | capitalize"
          @input="firstName = $event"
          rules="min:2|max:64|alpha"
          type="text"
          required
        ></BecomeField>
        <BecomeField
          name="birthdate"
          :label="$t('Contract.PersonalDetails.birthdate')"
          type="calendar"
          v-model="birthDate"
          :defaultDate="defaultDate"
          :calendarRules="birthDateRule"
          :required="customerType === 'resi'"
        ></BecomeField>
        <BecomeField
          name="phone"
          type="phone"
          :label="$t('Contract.PersonalDetails.phone.label')"
          :help="$t('Contract.PersonalDetails.phone.help')"
          :rules="phoneRule"
          v-model="phone"
          placeholder="498000000"
          required
        ></BecomeField>
        <BecomeField
          name="email"
          type="email"
          :label="$t('Contract.PersonalDetails.mail.label')"
          :help="$t('Contract.PersonalDetails.mail.help')"
          rules="email"
          v-model="mail"
          required
        ></BecomeField>
        <div v-if="customerType === 'pro'">
          <BecomeField
            name="vat_number"
            type="text"
            :label="$t('Situation.PersonalDetails.vatNumber.label')"
            :help="$t('Situation.PersonalDetails.vatNumber.help')"
            :value="vatNumber"
            @input="getEnterprise($event)"
            required
          ></BecomeField>
          <BecomeField
            name="companyName"
            type="text"
            :label="$t('Situation.PersonalDetails.companyName')"
            v-model="companyName"
            required
          ></BecomeField>
          <BecomeField
            name="legalForm"
            type="select"
            :label="$t('Situation.PersonalDetails.legalForm')"
            v-model="legalForm"
            :possibleValues="legalForms"
            required
          ></BecomeField>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import BecomeField from "@/components/Form/BecomeField";
import enterpriseService from "@/services/enterpriseService";
import inscriptionService from "@/services/inscriptionService";
import { mapState, mapGetters } from 'vuex';
import capitalize from "@/mixins/capitalize";

export default {
  mixins: [capitalize],
  computed: {
    ...mapState("subscription", ["customerType"]),
    ...mapGetters("subscription", ["isResidential", "isTestAchat"]),
    civility: {
      get() {
        return this.$store.state.subscription.civility;
      },
      set(value) {
        this.$store.commit("subscription/setCivility", value);
      },
    },
    civilities() {
      return [
        {
          label: this.$t("Situation.PersonalDetails.civilities.MA"),
          value: "MA",
        },
        {
          label: this.$t("Situation.PersonalDetails.civilities.MO"),
          value: "MO",
        },
      ];
    },
    lastName: {
      get() {
        return this.$store.state.subscription.lastName;
      },
      set(value) {
        this.$store.commit("subscription/setLastName", value);
        this.$store.commit("subscription/setHolderLastName", value);
      },
    },
    firstName: {
      get() {
        return this.$store.state.subscription.firstName;
      },
      set(value) {
        this.$store.commit("subscription/setFirstName", value);
        this.$store.commit("subscription/setHolderFirstName", value);
      },
    },
    birthDate: {
      get() {
        return this.$store.state.subscription.birthDate;
      },
      set(value) {
        this.$store.commit("subscription/setBirthDate", value);
      },
    },
    defaultDate() {
      let date = new Date();
      date.setFullYear(date.getFullYear() - 50);

      return date;
    },
    phone: {
      get() {
        return this.$store.state.subscription.phone;
      },
      set(value) {
        this.$store.commit("subscription/setPhone", value);
      },
    },
    phoneRule() {
      return this.phone.prefix === "+32" ? "be-phone" : "phone";
    },
    mail: {
      get() {
        return this.$store.state.subscription.mail;
      },
      set(value) {
        this.$store.commit("subscription/setMail", value);
      },
    },
    vatNumber: {
      get() {
        return this.$store.state.subscription.vatNumber;
      },
      set(value) {
        this.$store.commit("subscription/setVatNumber", value);
      },
    },
    companyName: {
      get() {
        return this.$store.state.subscription.companyName;
      },
      set(value) {
        this.$store.commit("subscription/setCompanyName", value);
      },
    },
    legalForm: {
      get() {
        return this.$store.state.subscription.legalForm;
      },
      set(value) {
        this.$store.commit("subscription/setLegalForm", value);
      },
    },
    legalForms() {
      let result = [];
      let legalForms = this.$store.state.common.legalForms;

      legalForms.forEach((legalForm) => {
        let line = {
          label: legalForm[this.$i18n.locale],
          value: legalForm.code,
        };

        result.push(line);
      });

      return result;
    },
  },
  methods: {
    async updateSubscription() {
      let payloadSub = {};

      payloadSub["civility"] = this.civility;
      payloadSub["first_name"] = this.firstName;
      payloadSub["last_name"] = this.lastName;

      // let phoneNumber = this.phone.number.replace(/ +/g, "");
      payloadSub[this.phone.type] = `${this.phone.prefix}${this.phone.number}`;
      
      payloadSub["email"] = this.mail;
      payloadSub["birth_date"] = this.birthDate ? this.$moment(this.birthDate, "DD/MM/YYYY").format("YYYY-MM-DD") : null;

      if (!this.isResidential) {
        payloadSub["vat_number"] = this.vatNumber;
        payloadSub["company_name"] = this.companyName;
        payloadSub["legal_form"] = this.legalForm;
      }

      try {
        await inscriptionService.updateSubscription(payloadSub);
        return true;
      } catch (error) {
        return error.response.data;
      }
    },
    async close() {
      let valid = await this.$refs.observer.validate();
      if (valid) {
        let serverValidation = await this.updateSubscription();

        if (serverValidation === true) {
          this.$emit('close');
        } else {
          Object.keys(serverValidation).forEach((key) => {
            key = key === 'mobile' ? 'phone' : key;
            let error = {};
            error[key] = this.$te(`Validation.${key}`) ? this.$t(`Validation.${key}`) : this.$t(`Validation.fallback`);
            this.$refs.observer.setErrors(error);
          });
        }
      }
    },
    birthDateRule(date) {
      const minor = new Date();
      minor.setHours(0, 0, 0, 0);
      minor.setFullYear(minor.getFullYear() - 14);

      const senior = new Date();
      senior.setHours(0, 0, 0, 0);
      senior.setFullYear(senior.getFullYear() - 120);

      return date < senior || date > minor;
    },
    async getEnterprise(value) {
      this.vatNumber = value;

      if (value.length > 6) {
        try {
          let response = await enterpriseService.get(value);
          this.companyName = response.name.fr || response.name.nl;
          this.legalForm = response.legal_form;
        } catch (error) {
          if (error.response.status === 400) {
            this.$refs.observer.setErrors({
              vat_number: this.$t("Validation.vat_number"),
            });
          }
        }
      }
    },
  },
  created() {
    if(!this.legalForms.length) this.$store.dispatch("common/getLegalForms");
  },
  components: {
    BecomeField,
  },
};
</script>

<style lang="scss" scoped>
.form {
  position: relative;

  .close {
    cursor: pointer;
    position: absolute;
    right: 15px;
    top: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    background: url("~@/assets/icons/icon-close.svg") no-repeat center/12px 12px $clearYellow;
    font-size: 0.8rem;
  }
}

.mobile.lastname {
  flex-basis: 100%!important;
  margin-left: 0!important;
}

.flex {
  flex: 1 !important;
}

.TA {
  .close {
    background: url("~@/assets/icons/icon-close-white.svg") no-repeat center/12px 12px $pink;
  }
}
</style>


// WEBPACK FOOTER //
// src/views/components/PersonalDetails.vue